import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap } from 'rxjs';
import { VchasnoKasaService } from '../../../services/vchasno-kasa.service';
import { BoardPaymentService } from '../../../services/board-payment.service';
import { FiscalNotificationService } from '../../../services/fiscal-notification.service';
import { canRetryFiscal, FiscalOperation, FiscalReturnPreview } from '../../prro/prro.types';
import { CardPayment } from '../payments.types';

@Component({
  selector: 'app-fiscal-return',
  standalone: false,
  templateUrl: './fiscal-return.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full min-h-0' },
})
export class FiscalReturnComponent implements OnInit {
  @Input({ required: true }) payment!: CardPayment;
  @Output() close = new EventEmitter<boolean>();
  private hasChanges = false;
  private readonly api = inject(VchasnoKasaService);
  private readonly feedback = inject(BoardPaymentService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly notifications = inject(FiscalNotificationService);
  protected readonly canRetry = canRetryFiscal;
  private inFlight = false;
  private polling = false;
  protected get busy(): boolean {
    return this.inFlight || this.polling;
  }
  protected get canClose(): boolean {
    return !this.inFlight;
  }
  protected loading = false;
  protected preview: FiscalReturnPreview | null = null;
  protected partial = false;
  protected quantities: Record<number, number> = {};
  protected advanceAmount = 0;
  protected submitted: FiscalOperation | null = null;
  private key = crypto.randomUUID();
  protected submittedBody: {
    idempotency_key: string;
    rows?: { index: number; quantity: number }[];
    amount?: number;
  } | null = null;

  protected get total(): number {
    const preview = this.preview;
    if (!preview) return 0;
    if (!this.partial) return preview.remaining;
    if (preview.advance) return this.advanceAmount;
    return preview.rows.reduce((sum, row) => {
      const quantity = Number(this.quantities[row.index] ?? 0);
      if (!Number.isFinite(quantity) || quantity < 0 || quantity > row.quantity || row.original_quantity <= 0)
        return sum;
      const full = BigInt(Math.round(row.original_quantity * 1000));
      const used = BigInt(Math.round(row.used_quantity * 1000));
      const next = used + BigInt(Math.round(quantity * 1000));
      const cents = BigInt(Math.round(row.total * 100));
      const allocate = (qty: bigint) => (cents * qty + full / 2n) / full;
      return sum + Number(allocate(next) - allocate(used)) / 100;
    }, 0);
  }

  protected get valid(): boolean {
    if (
      !this.preview ||
      this.preview.pending ||
      this.submitted ||
      this.loading ||
      this.total <= 0 ||
      this.total > this.preview.remaining + 0.00001
    )
      return false;
    return (
      !this.partial ||
      this.preview.advance ||
      this.preview.rows.every((row) => {
        const quantity = Number(this.quantities[row.index] ?? 0);
        return Number.isFinite(quantity) && quantity >= 0 && quantity <= row.quantity && Number.isInteger(quantity);
      })
    );
  }

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    if (this.loading) return;
    this.loading = true;
    this.api
      .returnPreview(this.payment.id)
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (preview) => {
          this.preview = preview;
          this.advanceAmount = preview.remaining;
          this.quantities = Object.fromEntries(preview.rows.map((row) => [row.index, row.quantity]));
        },
        error: (error) => this.feedback.error(error),
      });
  }

  requestClose(): void {
    if (!this.canClose) return;
    this.close.emit(this.hasChanges);
  }

  protected submit(): void {
    if (this.busy || (!this.submittedBody && !this.valid) || !this.preview) return;
    this.submittedBody ??= {
      idempotency_key: this.key,
      ...(this.partial
        ? this.preview.advance
          ? { amount: this.advanceAmount }
          : {
              rows: this.preview.rows
                .map((row) => ({ index: row.index, quantity: Number(this.quantities[row.index] ?? 0) }))
                .filter((row) => row.quantity > 0),
            }
        : {}),
    };
    this.inFlight = true;
    this.api
      .ensureOpenShift(this.preview.cash_register_id)
      .pipe(
        switchMap((opened) => (opened ? this.api.createReturn(this.payment.id, this.submittedBody!) : [])),
        finalize(() => (this.inFlight = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (operation) => {
          this.hasChanges = true;
          this.submitted = operation;
          this.wait(operation);
        },
        error: (error) => {
          this.feedback.error(error);
          // A definite validation response permits correction; transport failures must retain the original key/body.
          if ([400, 403, 404, 409].includes(error?.status)) {
            this.submittedBody = null;
            this.key = crypto.randomUUID();
            this.load();
          }
        },
      });
  }

  protected retry(operation: FiscalOperation): void {
    if (this.busy) return;
    this.inFlight = true;
    this.api
      .retryReceipt(operation)
      .pipe(
        finalize(() => (this.inFlight = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.hasChanges = true;
          this.submitted = result;
          this.wait(result);
        },
        error: (error) => this.feedback.error(error),
      });
  }

  protected check(): void {
    if (this.submitted && !this.busy) this.wait(this.submitted);
    else this.load();
  }

  private wait(operation: FiscalOperation): void {
    this.polling = true;
    this.api
      .waitForOperation(operation.id)
      .pipe(
        finalize(() => (this.polling = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.submitted = result;
          this.load();
          this.hasChanges = true;
          if (result.status === 'succeeded' || result.status === 'skipped') {
            this.submitted = null;
            this.submittedBody = null;
            this.key = crypto.randomUUID();
          }
        },
        error: (error) => this.feedback.error(error),
      });
  }
}
